import React, { useCallback, useEffect, useState } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { FIELD_TYPES, TOAST_SETTINGS_ERROR } from '../../../utility/constants'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EditFieldTemplateDialog from './EditFieldTemplateDialog'
import { listDataService } from '../../../services/listDataService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { getDefaultValueForField } from '../../DataEntryPage/utils/fieldUtil'
import { FilePreviewField } from '../../DataEntryPage/Fields/FilePreviewField'
import { FileUploadField } from '../../DataEntryPage/Fields/FileUploadField'
import { fileServices } from '../../../services/fileServices'
import moment from 'moment'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import SettingsIcon from '@mui/icons-material/Settings'
import { DeleteRounded } from '@mui/icons-material'
import adminFieldService from '../../../services/admin/field'
import { toast } from 'react-toastify'

const getRowsAccordingToDefaultValues = (subfields) => {
  let maxRows = 0
  subfields.forEach((subfield) => {
    const defaultValue = subfield.default_value
    const size = defaultValue?.values?.length || 0
    maxRows = maxRows > size ? maxRows : size
  })

  const rows = Array(maxRows)
    .fill(0)
    .map((_, i) => i + 1)

  const defaultValues = {}
  subfields.forEach((subfield) => {
    rows.forEach((row) => {
      defaultValues[`${subfield.id}__${row}`] = subfield?.default_value?.values
        ? subfield?.default_value?.values[row - 1]
        : getDefaultValueForField(subfield)
    })
  })

  return { rows, defaultValues }
}

const getDefaultValue = (defaultValue, index) => {
  if (defaultValue?.is_same_value) {
    return defaultValue?.values ? defaultValue?.values[0] : ''
  } else {
    return defaultValue?.values ? defaultValue?.values[index] : ''
  }
}

const getTabularDefaultValue = (subfields) => {
  // get rows based on default value
  const defaults = getRowsAccordingToDefaultValues(subfields)

  let data = []
  defaults.rows.forEach((row) => {
    let rowData = { row: row, sequence: row }
    subfields.forEach((sf) => {
      rowData = { ...rowData, [sf.id]: getDefaultValue(sf.default_value, row - 1) }
    })
    data.push(rowData)
  })

  return { values: data, rows: defaults.rows }
}

const getDbDefaultFromTabularDefaultValue = (tabularData) => {
  // sorting to support moving default values up & down
  const sorted = tabularData.sort((a, b) => a.sequence - b.sequence)

  let defaultValue = {}
  sorted.forEach((it) => {
    Object.entries(it).forEach(([key, value]) => {
      defaultValue[key] ? defaultValue[key].push(value) : (defaultValue[key] = [value])
    })
  })

  return defaultValue
}

const renderField = (subfield, row, listData, value, user, onChange) => {
  switch (subfield.data_structure) {
    case FIELD_TYPES.TEXT:
    case FIELD_TYPES.NUMBER:
    case FIELD_TYPES.CURRENCY:
    case FIELD_TYPES.BIGTEXT:
      return (
        <TextField
          disabled={
            subfield?.default_value ? subfield?.default_value.is_same_value && row > 1 : true
          }
          style={{ margin: '8px 0px', width: subfield.subfield_column_width_in_rem * 16 }}
          fullWidth
          size="small"
          label={`Default value`}
          placeholder={`No default value`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case FIELD_TYPES.LIST:
      return (
        <Autocomplete
          disabled={
            subfield?.default_value ? subfield?.default_value.is_same_value && row > 1 : true
          }
          options={listData[`list_data_${subfield.list_id}`] || []}
          style={{ margin: '8px 0px', width: subfield.subfield_column_width_in_rem * 16 }}
          getOptionLabel={(option) => option.value}
          fullWidth
          size="small"
          label={`Default value`}
          value={listData[`list_data_${subfield.list_id}`]?.find((it) => it.id == value) || null}
          onChange={(_, v) => onChange(v)}
          renderInput={(params) => <TextField {...params} label="Default value" />}
        />
      )
    case FIELD_TYPES.LIST_MULTI:
      return (
        <Autocomplete
          multiple
          disabled={
            subfield?.default_value ? subfield?.default_value.is_same_value && row > 1 : true
          }
          options={listData[`list_data_${subfield.list_id}`] || []}
          style={{ margin: '8px 0px', width: subfield.subfield_column_width_in_rem * 16 }}
          getOptionLabel={(option) => option?.value}
          fullWidth
          size="small"
          label={`Default value`}
          value={
            listData[`list_data_${subfield.list_id}`]?.filter((it) =>
              value?.split(',')?.includes('' + it.id)
            ) || []
          }
          onChange={(_v, v) => onChange(v, _v)}
          renderInput={(params) => <TextField {...params} label="Default value" />}
        />
      )
    case FIELD_TYPES.FILE:
      return value ? (
        <FilePreviewField
          disabled={
            subfield?.default_value ? subfield?.default_value.is_same_value && row > 1 : true
          }
          value={value}
          download={async () => await fileServices.downloadFile(user, value)}
          clear={() => onChange('')}
        />
      ) : (
        <FileUploadField
          disabled={
            subfield?.default_value ? subfield?.default_value.is_same_value && row > 1 : true
          }
          onChange={(e) => onChange(e.target.files[0])}
        />
      )
    case FIELD_TYPES.DATE:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DemoContainer sx={{ width: '100%', padding: 0 }} components={['DatePicker']}>
            <DatePicker
              placeholder={'Date'}
              value={value ? moment(value) : null}
              disabled={
                subfield?.default_value ? subfield?.default_value.is_same_value && row > 1 : true
              }
              format="DD MMM YYYY"
              onChange={(e) => onChange(e?.format('YYYY-MM-DD HH:mm:ss'))}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  variant: 'outlined',
                  color: 'secondary',
                },
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      )
    case FIELD_TYPES.DATETIME:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DemoContainer sx={{ width: '100%', padding: 0 }} components={['DateTimePicker']}>
            <DateTimePicker
              placeholder={'Date time'}
              value={value ? moment(value) : null}
              disabled={
                subfield?.default_value ? subfield?.default_value.is_same_value && row > 1 : true
              }
              format="DD MM YYYY, h:mm A"
              onChange={(e) => onChange(e?.format('YYYY-MM-DD HH:mm:ss'))}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  variant: 'outlined',
                  color: 'secondary',
                },
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      )
    default:
      console.log(`No such input fields ${subfield.data_structure}!`)
  }
}

const TabularFormDialog = ({
  fieldTemplate,
  ftData,
  onClose,
  importedFieldList,
  lists,
  tfData,
  atData,
  activityTemplateId,
  updateFieldTemplate,
  handleAddSubfield,
}) => {
  const { user } = useAuthContext()
  const [subfields, setSubfields] = useState([])
  const [dialogFtId, setDialogFtId] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [listData, setListData] = useState([])

  // new implementation
  const [tabularDefaultValues, setTabularDefaultValues] = useState([])

  useEffect(() => {
    let subfieldIds = []
    if (fieldTemplate.data_structure === FIELD_TYPES.TABLE) {
      if (ftData.find((ft) => ft.id == fieldTemplate.id).subfield_template_ids) {
        subfieldIds = ftData
          .find((ft) => ft.id == fieldTemplate.id)
          .subfield_template_ids.split(',')
          .map((elem) => Number(elem))
      }
    }

    const sftData = ftData.filter((ft) => subfieldIds.includes(ft.id))
    setSubfields(sftData)

    // initial default value
    const defaults = getTabularDefaultValue(sftData)
    setTabularDefaultValues(defaults.values)
  }, [fieldTemplate, ftData])

  useEffect(() => {
    ;(async () => {
      const data = await listDataService.getDataActivity(user, activityTemplateId)
      setListData(data)
    })()
  }, [activityTemplateId, fieldTemplate, user])

  const getValueForUI = useCallback(
    (row, subfield) => {
      if (subfield?.default_value?.is_same_value == true) {
        row = tabularDefaultValues[0].row
      }

      const rowData = tabularDefaultValues.find((it) => it.row == row)
      return rowData ? rowData[subfield.id] : ''
    },
    [tabularDefaultValues]
  )

  const setValueToTabularValues = (row, subfieldId, value) => {
    setTabularDefaultValues(
      tabularDefaultValues.map((it) => (it.row === row ? { ...it, [subfieldId]: value } : it))
    )
  }

  const saveChanges = async () => {
    try {
      const dValues = getDbDefaultFromTabularDefaultValue(tabularDefaultValues)
      // update multiple fields in loop
      const updatedFields = subfields
        .filter((sf) => dValues[sf.id] || dValues[sf.id]?.length > 0)
        .map((sf) => {
          return {
            ...sf,
            default_value: {
              ...sf.default_value,
              values: sf.default_value?.is_same_value ? [dValues[sf.id][0]] : dValues[sf.id],
            },
          }
        })

      await adminFieldService.updateSubfieldDefaultInBulk(user, updatedFields)
      onClose()
      toast.success('Default values saved successfully!')
    } catch (error) {
      toast.error(error.message, TOAST_SETTINGS_ERROR)
    }
  }

  return (
    <>
      {
        <DialogWrapper
          fullWidth
          maxWidth="md"
          open={!isEditDialogOpen}
          onClose={onClose}
          heading={`${fieldTemplate?.field_template_name} (Table Field Setting)`}
          actions={null}
        >
          {/* Setup Form Dialog */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Seq.</TableCell>
                  {subfields?.map((subfield) => {
                    return (
                      <TableCell>
                        <Typography variant="p">{subfield.field_template_name}</Typography>{' '}
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tooltip title="Edit Field">
                            <IconButton
                              style={{ padding: 0 }}
                              name={`f_${subfield.id}`}
                              onClick={() => {
                                setDialogFtId(subfield.id)
                                setIsEditDialogOpen(true)
                              }}
                              size="small"
                            >
                              <SettingsIcon style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Have default value?">
                            <Checkbox
                              style={{ padding: 0 }}
                              color="primary"
                              checked={subfield?.default_value != null}
                              onClick={() => {
                                const _defaultValue =
                                  subfield?.default_value == null
                                    ? { is_same_value: true, values: [] }
                                    : null

                                setSubfields(
                                  subfields.map((field) =>
                                    field.id == subfield.id
                                      ? {
                                          ...field,
                                          default_value: _defaultValue,
                                        }
                                      : field
                                  )
                                )

                                setTabularDefaultValues(
                                  tabularDefaultValues.map((it) => ({
                                    ...it,
                                    [subfield.id]: [],
                                  }))
                                )
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Is same value?">
                            <Checkbox
                              style={{ padding: 0 }}
                              color="primary"
                              checked={subfield?.default_value?.is_same_value || false}
                              onClick={() => {
                                const df = {
                                  ...subfield?.default_value,
                                  is_same_value: !subfield?.default_value?.is_same_value,
                                  values: !subfield?.default_value?.is_same_value
                                    ? [subfield?.default_value?.values[0]]
                                    : subfield?.default_value?.values,
                                }

                                setSubfields(
                                  subfields.map((field) =>
                                    field.id == subfield.id
                                      ? {
                                          ...field,
                                          default_value: df,
                                        }
                                      : field
                                  )
                                )
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )
                  })}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tabularDefaultValues.map((rowData, index) => {
                  return (
                    <TableRow id={rowData.row}>
                      <TableCell>
                        <TextField
                          style={{ minWidth: 48 }}
                          disabled={false}
                          type="number"
                          size="small"
                          value={rowData.sequence}
                          onChange={(e) =>
                            setValueToTabularValues(rowData.row, 'sequence', e.target.value)
                          }
                          placeholder="Seq."
                        />
                      </TableCell>
                      {subfields?.map((subfield) => {
                        return (
                          <TableCell
                            key={subfield.id + '-' + rowData.row}
                            style={{ width: subfield.subfield_column_width_in_rem * 16 }}
                          >
                            <Box style={{ margin: '4px 0px' }}>
                              {renderField(
                                subfield,
                                index + 1,
                                listData,
                                getValueForUI(rowData.row, subfield),
                                user,
                                async (value, __value) => {
                                  let _v = value

                                  if (subfield.data_structure == FIELD_TYPES.LIST) {
                                    _v = value?.id
                                  }

                                  if (subfield.data_structure == FIELD_TYPES.LIST_MULTI) {
                                    _v = value?.map((it) => it.id).join(',')
                                  } else if (subfield.data_structure == FIELD_TYPES.FILE) {
                                    const result = (await fileServices.uploadFile(user, value)).data
                                    _v = result?.filePath?.split('/')[1] || ''
                                  }

                                  setValueToTabularValues(rowData.row, subfield.id, _v)
                                }
                              )}
                            </Box>
                          </TableCell>
                        )
                      })}
                      <TableCell>
                        <Tooltip title="Delete Row">
                          <IconButton
                            onClick={() => {
                              setTabularDefaultValues(
                                tabularDefaultValues.filter((it) => it.row !== rowData.row)
                              )
                            }}
                          >
                            <DeleteRounded style={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
            }}
          >
            <Box>
              <Button
                variant="outlined"
                style={{ marginTop: 8 }}
                color="primary"
                onClick={() => {
                  const newRow = tabularDefaultValues?.length + 1
                  setTabularDefaultValues([
                    ...tabularDefaultValues,
                    { row: newRow, sequence: newRow },
                  ])
                }}
              >
                Add Row
              </Button>
              <Button
                variant="outlined"
                style={{ marginTop: 8, marginLeft: 8 }}
                color="primary"
                onClick={() => handleAddSubfield(fieldTemplate.id, fieldTemplate.page_template_id)}
              >
                Add Subfield
              </Button>
            </Box>
            <Button
              size="small"
              style={{ marginTop: 8 }}
              color="primary"
              variant="contained"
              onClick={saveChanges}
            >
              Save Changes
            </Button>
          </Box>
        </DialogWrapper>
      }
      {/* Edit Field Properties Dialog */}
      {isEditDialogOpen && (
        <EditFieldTemplateDialog
          initialFieldTemplate={ftData.find((it) => it.id == dialogFtId)}
          importedFieldList={importedFieldList}
          lists={lists}
          tfData={tfData}
          ftData={ftData}
          setDialogOpen={setIsEditDialogOpen}
          processTemplateId={atData?.process_template_id}
          activityTemplateId={activityTemplateId}
          activityTemplate={atData}
          onChangeSave={(fieldTemplate) => {
            updateFieldTemplate(fieldTemplate, false)
            setSubfields(subfields.map((it) => (it.id == fieldTemplate.id ? fieldTemplate : it)))
          }}
          onFieldDelete={() => {}}
        />
      )}
    </>
  )
}

export default TabularFormDialog
